import React, { useState, useEffect } from "react";
import dayjs from "dayjs";
import useDataSourceManager from "hooks/useDataSourceManager";
import { v4 as uuidv4 } from "uuid";
import mainStyle from "../../../../mainStyle.module.scss";
import { StyledSelect } from "components/Select/searchWithSelect";
import { MenuSelect } from "components/Select/menuSelect";
import {
  Box,
  Button,
  Typography,
  Divider,
  CircularProgress,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";

import { SolidButton } from "components/buttons/SolidButton";
import { OutlineButton } from "components/buttons/OutlineButton";
import { OutlineRectangleButton } from "components/buttons/OutlineRectangleButton";
import ModalContainer from "components/ModalContainer";
import CollapseCard from "components/Collapse";

import { ErrorNotification } from "pages/System/UserManagement/ErrorHandle";

import { ReactComponent as UnverifiedIcon } from "assets/images/icons/icn_table_cell_Unverified.svg";
import { ReactComponent as BinIcon } from "assets/images/icons/icn_table_cell_Bin.svg";
import { ReactComponent as BinHoverIcon } from "assets/images/icons/icn_table_cell_Bin_hover.svg";
import { ReactComponent as AddIcon } from "assets/images/icons/icn_table_cell_Add.svg";
import { ReactComponent as AddHoverIcon } from "assets/images/icons/icn_table_cell_Add_hover.svg";
import { ReactComponent as CloseIcon } from "assets/images/icons/icn_close.svg";
import { ReactComponent as EmailIcon } from "assets/images/icons/icn_table_cell_Email.svg";
import { ReactComponent as EmailHoverIcon } from "assets/images/icons/icn_table_cell_Email_hover.svg";
import { ReactComponent as AvatarNoneIcon } from "assets/images/icons/img_photo_none.svg";

const EditInvitation = (props) => {
  const {
    setSelectedRowData,
    selectedRowData,
    drawerOpen,
    setDrawerOpen,
    setOpenDeleteAccountModal,
    handleOpenResendModal,
    allOrgNames,
    orgRoleHierarchy,
    mspRoleHierarchy,
    entId,
    currentUserMspRoleId,
    getListEnterpriseUsers,
    getListPendingEnterpriseUsers,
    isViewer,
    getInitials,
    formatStatus,
    originalRowData,
  } = props;

  const theme = useTheme();

  const supportSession = JSON.parse(
    sessionStorage.getItem("supportSession") || "null",
  );

  const accessLevel = supportSession?.accessLevel;

  const isSupportMode =
    accessLevel === "full_control" || accessLevel === "read_only";

  const isSupporter = JSON.parse(
    localStorage.getItem("is_supporter") || "null",
  );

  const [isDeleteHovered, setIsDeleteHovered] = useState(false);
  const [isAddHovered, setIsAddHovered] = useState(false);
  const [emailhover, setEmailHover] = useState(null);
  const [updateUuid, setUpdateUuid] = useState(uuidv4());
  const [errorMessage, setErrorMessage] = useState("");
  const [errorMessageHandleModal, setErrorMessageHandleModal] = useState(false);
  const [actionModal, setActionModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const { dataMap, getData, decodeTokenData, sendRequest, sendBatch } =
    useDataSourceManager();

  const isLowerRoleThanMe =
    selectedRowData?.mspRoleId != null &&
    currentUserMspRoleId != null &&
    currentUserMspRoleId <= selectedRowData.mspRoleId;

  const isSaveDisabled =
    selectedRowData?.orgAccesses?.some(
      (site) => !site || !site.orgName || !site.orgRoleName,
    ) ||
    (selectedRowData?.mspRoleId === 3 &&
      selectedRowData?.orgAccesses?.length === 0);

  const handleCloseModal = () => {
    setSelectedRowData(originalRowData);
  };

  const handleSaveChanges = () => {
    const updates = {
      mspRoleId: selectedRowData.mspRoleId,
      orgAccesses: selectedRowData.orgAccesses,
    };

    setIsLoading(true);

    sendRequest(
      "ents/:entId/invitations/:invitationId.update",
      updateUuid,
      {
        entId,
        invitationId: selectedRowData.invitationId,
      },
      {
        invitationId: selectedRowData.invitationId,
        updates,
      },
    );
  };

  useEffect(() => {
    const updateInvitationResponse = getData(updateUuid);

    if (!updateInvitationResponse) return;

    setIsLoading(false);

    if (updateInvitationResponse?.code === 400) {
      setActionModal(true);
      setErrorMessage(updateInvitationResponse?.message);
    } else {
      getListEnterpriseUsers();

      /* getListPendingEnterpriseUsers(); */

      setDrawerOpen(false);
    }
  }, [dataMap]);

  const handleOpenDeleteAccountModal = () => {
    setOpenDeleteAccountModal(true);
  };

  const handleChange = (index, field, value) => {
    if (field === "mspRole") {
      setSelectedRowData((prevData) => {
        if (
          value?.orgRoleName === "Admin" ||
          value?.orgRoleName === "Super Admin"
        ) {
          return {
            ...prevData,
            mspRoleName: value.orgRoleName,
            mspRoleId: value.orgRoleId,
            orgAccesses: [],
          };
        } else {
          return {
            ...prevData,
            mspRoleName: value.orgRoleName,
            mspRoleId: value.orgRoleId,
          };
        }
      });
    } else if (field === "role") {
      // Organization Privilege 不允許 Super Admin
      if (value?.orgRoleName === "Super Admin") return;

      setSelectedRowData((prevData) => {
        const updated = [...prevData.orgAccesses];

        updated[index] = {
          ...updated[index],
          orgRoleId: value.orgRoleId,
          orgRoleName: value.orgRoleName,
        };

        return {
          ...prevData,
          orgAccesses: updated,
        };
      });
    } else if (field === "org") {
      setSelectedRowData((prevData) => {
        const updated = [...prevData.orgAccesses];

        updated[index] = {
          ...updated[index],
          orgId: value.orgId,
          orgName: value.orgName,
          orgRoleId: value.orgRoleId,
          orgRoleName: value.orgRoleName,
        };

        return {
          ...prevData,
          orgAccesses: updated,
        };
      });
    }
  };

  const handleDelete = (index) => {
    setSelectedRowData((prevData) => {
      const updatedAccess = prevData.orgAccesses.filter((_, i) => i !== index);

      return {
        ...prevData,
        orgAccesses: updatedAccess,
      };
    });
  };

  const handleAddClick = () => {
    setSelectedRowData((prevData) => {
      const updatedAccess = [
        ...prevData.orgAccesses,
        {
          orgName: "",
          orgId: "",
          orgRoleName: "",
          orgRoleId: null,
        },
      ];

      return {
        ...prevData,
        orgAccesses: updatedAccess,
      };
    });
  };

  return (
    <>
      <Box>
        {isLoading && (
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              backgroundColor: "rgba(255,255,255,0.6)",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              zIndex: 9999,
            }}
          >
            <CircularProgress
              sx={{
                color: theme.palette.primary.mainHover,
              }}
            />
          </Box>
        )}

        <Box
          sx={{
            paddingLeft: "25px",
            display: "flex",
            alignItems: "center",
            gap: 2,
          }}
        >
          {selectedRowData && (
            <>
              {selectedRowData.avatar ? (
                <img
                  src={selectedRowData.avatar}
                  alt="User"
                  className={mainStyle["avatar-with-photo"]}
                />
              ) : selectedRowData.name ? (
                <div className={mainStyle["avatar-without-photo-has-name"]}>
                  {getInitials(selectedRowData.name)}
                </div>
              ) : (
                <div className={mainStyle["avatar-without-photo"]}>
                  <AvatarNoneIcon />
                </div>
              )}

              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                }}
              >
                {selectedRowData.email && (
                  <span
                    style={{
                      fontWeight: theme.typography.customFontWeight[3],
                      fontSize: theme.typography.customFontSize[5],
                      color: theme.palette.primary.greyPrimary,
                    }}
                  >
                    {selectedRowData.email}
                  </span>
                )}

                {selectedRowData.status && (
                  <span
                    style={{
                      color:
                        selectedRowData.status === "pending" ||
                        selectedRowData.status === "expired"
                          ? theme.palette.primary.redPrimary
                          : selectedRowData.status === "active"
                            ? theme.palette.primary.greenPrimary
                            : selectedRowData.status === "inactive"
                              ? theme.palette.primary.greySecound
                              : theme.palette.primary.greyPrimary,

                      border: `1.5px solid ${
                        selectedRowData.status === "pending" ||
                        selectedRowData.status === "expired"
                          ? theme.palette.primary.redPrimary
                          : selectedRowData.status === "active"
                            ? theme.palette.primary.greenPrimary
                            : selectedRowData.status === "inactive"
                              ? theme.palette.primary.greySecound
                              : theme.palette.primary.greyPrimary
                      }`,

                      padding: "4px 12px",
                      width: "91px",
                      height: "27px",
                      gap: "10px",
                      borderRadius: theme.shape.customBorderRadius[7],
                      fontSize: theme.typography.customFontSize[0],
                      justifyContent: "center",
                      alignItems: "center",
                      textAlign: "center",
                      display: "flex",
                    }}
                  >
                    {selectedRowData.status === undefined
                      ? "-"
                      : formatStatus(selectedRowData.status)}
                  </span>
                )}
              </Box>
            </>
          )}
        </Box>
      </Box>

      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "100%",
        }}
      >
        <Box
          sx={{
            flex: 1,
            overflowY: "auto",
            p: 2,
          }}
        >
          <Box
            sx={{
              width: "100%",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              p: 2,
              boxSizing: "border-box",
            }}
          >
            <CollapseCard title="User Information">
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 2,
                }}
              >
                {selectedRowData && (
                  <>
                    <Box
                      sx={{
                        display: "flex",
                        gap: 2,
                        alignItems: "center",
                        width: "100%",
                      }}
                    >
                      <Typography
                        className={
                          mainStyle["user-management-modal-typography"]
                        }
                      >
                        Account Status
                      </Typography>

                      <Button
                        disabled={isSupportMode || isSupporter}
                        onMouseEnter={() => {
                          if (!isSupportMode || !isSupporter) {
                            setEmailHover(true);
                          }
                        }}
                        onMouseLeave={() => setEmailHover(false)}
                        onClick={
                          isSupportMode || isSupporter
                            ? undefined
                            : handleOpenResendModal
                        }
                        endIcon={
                          emailhover && (!isSupportMode || !isSupporter) ? (
                            <EmailHoverIcon />
                          ) : (
                            <EmailIcon />
                          )
                        }
                        sx={{
                          color: theme.palette.primary.black,
                          fontSize: theme.typography.customFontSize[2],
                          fontFamily: theme.typography.fontFamily,
                          fontWeight: theme.typography.customFontWeight[1],
                          backgroundColor: theme.palette.primary.transparent,
                          marginLeft: "-8px",
                          minWidth: "auto",
                          lineHeight: 1,
                          display: "flex",
                          alignItems: "center",
                          textTransform: "capitalize",

                          ...((isSupportMode || isSupporter) && {
                            filter: "grayscale(100%)",
                            opacity: 0.4,
                            cursor: "not-allowed",
                            pointerEvents: "none",
                          }),

                          "&:hover": {
                            backgroundColor: theme.palette.primary.transparent,
                            color: theme.palette.primary.mainHover,
                          },

                          "&:active": {
                            backgroundColor: theme.palette.primary.transparent,
                            color: theme.palette.primary.main,
                          },

                          "&.Mui-disabled": {
                            color: theme.palette.primary.greySecound,
                          },
                        }}
                      >
                        {selectedRowData.status}
                      </Button>
                    </Box>

                    <Box
                      sx={{
                        display: "flex",
                        gap: 3,
                        alignItems: "center",
                        width: "100%",
                      }}
                    >
                      <Typography
                        className={
                          mainStyle["user-management-modal-typography"]
                        }
                      >
                        Name
                      </Typography>

                      <Typography
                        sx={{
                          fontSize: theme.typography.customFontSize[2],
                          fontFamily: theme.typography.fontFamily,
                          fontWeight: theme.typography.customFontWeight[1],
                        }}
                      >
                        {selectedRowData.name || "-"}
                      </Typography>
                    </Box>

                    <Box
                      sx={{
                        display: "flex",
                        gap: 3,
                        alignItems: "center",
                        width: "100%",
                      }}
                    >
                      <Typography
                        className={
                          mainStyle["user-management-modal-typography"]
                        }
                      >
                        MSP Role
                      </Typography>

                      {isViewer ||
                      !isLowerRoleThanMe ||
                      isSupportMode ||
                      isSupporter ? (
                        <Typography
                          sx={{
                            fontSize: theme.typography.customFontSize[2],
                            fontFamily: theme.typography.fontFamily,
                            fontWeight: theme.typography.customFontWeight[1],
                          }}
                        >
                          {selectedRowData.mspRoleName}
                        </Typography>
                      ) : (
                        <MenuSelect
                          value={selectedRowData.mspRoleName}
                          onChange={(opt) => handleChange(null, "mspRole", opt)}
                          options={(() => {
                            if (
                              !currentUserMspRoleId ||
                              typeof currentUserMspRoleId !== "number"
                            ) {
                              return [];
                            }

                            return mspRoleHierarchy
                              .filter(
                                (role) =>
                                  role.mspRoleId >= currentUserMspRoleId,
                              )
                              .map((role) => ({
                                label: role.mspRoleName,
                                value: role.mspRoleName,
                                orgRoleId: role.mspRoleId,
                                orgRoleName: role.mspRoleName,
                              }));
                          })()}
                          width="100%"
                        />
                      )}
                    </Box>

                    <Box
                      sx={{
                        display:
                          selectedRowData.mspRoleName === "Viewer"
                            ? "block"
                            : "flex",

                        gap: isLowerRoleThanMe ? 3 : 2.5,

                        alignItems: "center",
                        width: "100%",
                      }}
                    >
                      <Typography
                        sx={{
                          color: theme.palette.primary.greyPrimary,
                          fontSize: theme.typography.customFontSize[1],
                          fontWeight: theme.typography.customFontWeight[1],
                          letterSpacing: "0.16px",
                          fontFamily: theme.typography.fontFamily,
                          mb: selectedRowData.mspRoleName === "Viewer" ? 1 : 0,
                        }}
                      >
                        Organization Privilege
                      </Typography>

                      {selectedRowData.mspRoleName === "Super Admin" ||
                      selectedRowData.mspRoleName === "Admin" ? (
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            fontFamily: theme.typography.fontFamily,
                            fontSize: theme.typography.customFontSize[2],
                            color: theme.palette.primary.black,
                            fontWeight: theme.typography.customFontWeight[1],
                          }}
                        >
                          All Organizations
                        </Box>
                      ) : (
                        <>
                          <Box
                            sx={{
                              display: "flex",
                              width: "100%",
                              mt: 3,
                            }}
                          >
                            <Box
                              sx={{
                                flex: 1,
                                display: "flex",
                                color: theme.palette.primary.greyThird,
                                fontSize: theme.typography.customFontSize[3],
                                fontWeight:
                                  theme.typography.customFontWeight[2],
                                fontFamily: theme.typography.fontFamily,
                              }}
                            >
                              Organization
                            </Box>

                            <Box
                              sx={{
                                flex: 1,
                                display: "flex",
                                color: theme.palette.primary.greyThird,
                                fontSize: theme.typography.customFontSize[3],
                                fontWeight:
                                  theme.typography.customFontWeight[2],
                                fontFamily: theme.typography.fontFamily,
                              }}
                            >
                              Role
                            </Box>
                          </Box>

                          <Divider
                            sx={{
                              marginTop: "10px",
                              borderColor: theme.palette.primary.greyThread,
                              borderWidth: 1,
                            }}
                          />

                          {selectedRowData.orgAccesses?.map((site, index) => (
                            <Box
                              sx={{
                                marginTop: "20px",
                                display: "flex",
                                width: "100%",
                              }}
                              key={index}
                            >
                              <Box
                                sx={{
                                  flex: 1,
                                  display: "flex",
                                  color: theme.palette.primary.greyThird,
                                  fontSize: theme.typography.customFontSize[3],
                                  fontWeight:
                                    theme.typography.customFontWeight[2],
                                  fontFamily: theme.typography.fontFamily,
                                }}
                              >
                                {isViewer ? (
                                  <Typography
                                    sx={{
                                      fontFamily: theme.typography.fontFamily,
                                      fontSize:
                                        theme.typography.customFontSize[2],
                                      color: theme.palette.primary.greyPrimary,
                                    }}
                                  >
                                    {site.orgName}
                                  </Typography>
                                ) : (
                                  <StyledSelect
                                    value={site.orgName}
                                    onChange={(opt) =>
                                      handleChange(index, "org", opt)
                                    }
                                    options={allOrgNames.map((s) => ({
                                      ...s,
                                      value: s.orgName,
                                      label: s.orgId,
                                    }))}
                                    disabledOptions={selectedRowData.orgAccesses
                                      .filter((s, idx) => idx !== index)
                                      .map((s) => s.orgName)}
                                    placeholder="Select Org"
                                    width="100%"
                                  />
                                )}
                              </Box>

                              <Box
                                sx={{
                                  flex: 1,
                                  display: "flex",
                                  color: theme.palette.primary.greyThird,
                                  fontSize: theme.typography.customFontSize[3],
                                  fontWeight:
                                    theme.typography.customFontWeight[2],
                                  fontFamily: theme.typography.fontFamily,
                                  alignItems: "center",
                                }}
                              >
                                {isViewer ? (
                                  <Typography
                                    sx={{
                                      fontFamily: theme.typography.fontFamily,
                                      fontSize:
                                        theme.typography.customFontSize[2],
                                      color: theme.palette.primary.greyPrimary,
                                      marginLeft: "-10px",
                                    }}
                                  >
                                    {site.orgRoleName}
                                  </Typography>
                                ) : (
                                  <>
                                    <MenuSelect
                                      value={site.orgRoleName}
                                      onChange={(opt) =>
                                        handleChange(index, "role", opt)
                                      }
                                      options={
                                        !site.orgName
                                          ? []
                                          : orgRoleHierarchy
                                              .filter(
                                                (role) =>
                                                  role.orgRoleName !==
                                                  "Super Admin",
                                              )
                                              .map((role) => ({
                                                label: role.orgRoleName,
                                                value: role.orgRoleName,
                                                orgRoleId: role.orgRoleId,
                                                orgRoleName: role.orgRoleName,
                                              }))
                                      }
                                      width="100%"
                                      placeholder="Select Role"
                                      disabled={!site.orgName}
                                    />

                                    {index > 0 && (
                                      <Button
                                        sx={{
                                          padding: 0,
                                          minWidth: 0,
                                          backgroundColor:
                                            theme.palette.primary.transparent,
                                          boxShadow: "none",

                                          "&:hover": {
                                            backgroundColor:
                                              theme.palette.primary.transparent,
                                          },
                                        }}
                                        onClick={() => handleDelete(index)}
                                      >
                                        <UnverifiedIcon />
                                      </Button>
                                    )}
                                  </>
                                )}
                              </Box>
                            </Box>
                          ))}

                          {!isViewer && (
                            <Box
                              sx={{
                                marginTop: "20px",
                                display: "flex",
                                justifyContent: "flex-end",
                              }}
                            >
                              <OutlineRectangleButton
                                onMouseEnter={() => setIsAddHovered(true)}
                                onMouseLeave={() => setIsAddHovered(false)}
                                startIcon={
                                  isAddHovered ? <AddHoverIcon /> : <AddIcon />
                                }
                                width="54px"
                                height="28px"
                                onClick={handleAddClick}
                              >
                                Add
                              </OutlineRectangleButton>
                            </Box>
                          )}
                        </>
                      )}
                    </Box>
                  </>
                )}
              </Box>
            </CollapseCard>
          </Box>

          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              pl: 1,
              pr: 2,
            }}
          >
            <Box
              sx={{
                flex: 1,
              }}
            >
              {!isViewer && isLowerRoleThanMe && (
                <Button
                  disabled={isSupportMode || isSupporter}
                  onMouseEnter={() => {
                    if (!isSupportMode || !isSupporter) {
                      setIsDeleteHovered(true);
                    }
                  }}
                  onMouseLeave={() => setIsDeleteHovered(false)}
                  startIcon={
                    isSupportMode || isSupporter ? (
                      <BinIcon />
                    ) : isDeleteHovered ? (
                      <BinHoverIcon />
                    ) : (
                      <BinIcon />
                    )
                  }
                  onClick={
                    isSupportMode || isSupporter
                      ? undefined
                      : handleOpenDeleteAccountModal
                  }
                  sx={{
                    color: theme.palette.primary.main,
                    fontSize: theme.typography.customFontSize[3],
                    fontFamily: theme.typography.fontFamily,
                    backgroundColor: theme.palette.primary.transparent,

                    ...((isSupportMode || isSupporter) && {
                      filter: "grayscale(100%)",
                      opacity: 0.4,
                      cursor: "not-allowed",
                      pointerEvents: "none",
                    }),

                    "&:hover": {
                      backgroundColor: theme.palette.primary.transparent,
                      color:
                        isSupportMode || isSupporter
                          ? theme.palette.primary.main
                          : theme.palette.primary.mainHover,
                    },

                    "&:active": {
                      backgroundColor: theme.palette.primary.transparent,
                      color: theme.palette.primary.main,
                    },
                  }}
                >
                  Delete User
                </Button>
              )}
            </Box>

            <Box
              sx={{
                flex: 1,
                display: "flex",
                justifyContent: "flex-end",
                alignItems: "center",
              }}
            >
              <Typography
                sx={{
                  fontSize: theme.typography.customFontSize[1],
                  color: theme.palette.primary.greySecound,
                  fontFamily: theme.typography.fontFamily,
                  whiteSpace: "nowrap",
                }}
              >
                Joined On:
                <span
                  style={{
                    color: theme.palette.primary.mainHover,
                  }}
                >
                  {dayjs
                    .unix(selectedRowData?.timeOfInvitation)
                    .format("YYYY/MM/DD HH:mm:ss")}
                </span>
              </Typography>
            </Box>
          </Box>
        </Box>

        {!isViewer && isLowerRoleThanMe && (
          <Box
            sx={{
              margin: "0 auto",
              display: "flex",
              justifyContent: "center",
            }}
          >
            <Box
              sx={{
                marginRight: "20px",

                ...((isSupportMode || isSupporter) && {
                  filter: "grayscale(100%)",
                  opacity: 0.4,
                  pointerEvents: "none",
                }),
              }}
            >
              <OutlineButton
                onClick={
                  isSupportMode || isSupporter ? undefined : handleCloseModal
                }
                disabled={isSupportMode || isSupporter}
                width="100px"
                height="32px"
              >
                Cancel
              </OutlineButton>
            </Box>

            <Box
              sx={{
                ...((isSupportMode || isSupporter) && {
                  filter: "grayscale(100%)",
                  opacity: 0.4,
                  pointerEvents: "none",
                }),
              }}
            >
              <SolidButton
                onClick={
                  isSupportMode || isSupporter ? undefined : handleSaveChanges
                }
                disabled={isSaveDisabled || isSupportMode || isSupporter}
                width="100px"
                height="32px"
              >
                Apply
              </SolidButton>
            </Box>
          </Box>
        )}
      </Box>

      <ModalContainer display={actionModal} modalWidth="500px">
        <Box
          sx={{
            textAlign: "center",
          }}
        >
          <Typography
            sx={{
              color: theme.palette.primary.main,
              fontSize: theme.typography.customFontSize[5],
              fontWeight: theme.typography.customFontWeight[3],
              fontFamily: theme.typography.fontFamily,
            }}
          >
            Action denied
          </Typography>

          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
            }}
          >
            <Typography
              sx={{
                color: theme.palette.primary.black,
                fontSize: theme.typography.customFontSize[3],
                fontFamily: theme.typography.fontFamily,
                marginTop: "20px",
              }}
            >
              {errorMessage}
            </Typography>
          </Box>

          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
            }}
          >
            <SolidButton
              sx={{
                marginTop: "30px",
              }}
              onClick={() => setActionModal(false)}
              width="100px"
              height="32px"
            >
              OK
            </SolidButton>
          </Box>
        </Box>
      </ModalContainer>

      <ErrorNotification
        open={errorMessageHandleModal}
        onClose={() => setErrorMessageHandleModal(false)}
        message={errorMessage}
      />
    </>
  );
};

export default EditInvitation;
